import * as React from "react";
import * as moment from "moment";
import { Link, LinkProps } from "react-router-dom";
import {
    capitalize,
    getCourtesyCarLevelIncludedText,
    getCursorClassName,
    getGHCIncludeDescription,
    getGuaranteedHireCarText,
    getIsOptionalExtraIncludedText,
    getLink
} from "../../../../../CommonFunctions";
import { ButtonLink } from "../../../../../components/navigation/ButtonLink";
import { InfoModal } from "../../../../../components/modals/InfoModal";
import {
    AnnualPolicyInfo,
    BreakdownCoverLevelEnum,
    GreenCard,
    InteractionHelper,
    resourcesHelper,
    Subscription
} from "@falcon/services";

const Section: React.FC<React.HTMLProps<HTMLDivElement>> = ({className, children}) => (
    <div className={`section ${className || ""}`}>{children}</div>
);

const SectionHeading: React.FC<React.HTMLProps<HTMLDivElement>> = ({className, children}) => (
    <div className={`section-heading text-blue-green font-weight-semi-bold ${className || ""}`}>{children}</div>
);

const SectionContents: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="section-contents">{children}</div>
);

const Detail: React.FC<React.HTMLProps<HTMLDivElement>> = ({children, className}) => (
    <div className={`detail ${className || ""}`}>{children}</div>
);

const DetailKey: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-key">{children}</div>
);

const DetailValue: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-value">{children}</div>
);

const DetailValuePrice: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="detail-value font-weight-semi-bold text-blue-1">{children}</div>
);

const SectionLinks: React.FC<React.PropsWithChildren> = ({children}) => (
    <div className="section-links">{children}</div>
);

const PanelLink: React.FC<LinkProps> = ({
                                            id, "aria-label": ariaLabel, to,
                                            children, className
                                        }) => (
    <Link
        id={id}
        className={`text-blue-2 ${className}`}
        aria-label={ariaLabel}
        to={to}
    >{children}</Link>
);

const LinkHighlight: React.FC<React.PropsWithChildren> = ({children}) => (
    <span className="text-blue-1 font-weight-semi-bold text-decoration-underline">{children}</span>
);

const renderWolfExcessTableExplained = (excesses: any, excessType: string) => (
    <table className={`table table-responsive table-striped excess-table ${excessType}`}>
        <thead>
        <tr>
            <th scope="col"/>
            <th scope="col">Compulsory excess</th>
            <th scope="col">Voluntary excess</th>
            <th scope="col">Total</th>
        </tr>
        </thead>
        <tbody>
        <tr>
            <td className="font-weight-semi-bold">Fire</td>
            <td>£{excesses.fireExcess}*</td>
            <td>£{excesses.voluntaryExcess}</td>
            <td>£{excesses.totalFire}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Theft</td>
            <td>£{excesses.theftExcess}*</td>
            <td>£{excesses.voluntaryExcess}</td>
            <td>£{excesses.totalTheft}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Accidental damage</td>
            <td>£{excesses.adExcess}*</td>
            <td>£{excesses.voluntaryExcess}</td>
            <td>£{excesses.totalAd}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Glass replacement</td>
            <td>£{excesses.windscreenReplacementExcess}</td>
            <td>-</td>
            <td>£{excesses.windscreenReplacementExcess}</td>
        </tr>
        <tr>
            <td className="font-weight-semi-bold">Glass repair</td>
            <td>£{excesses.windscreenRepairExcess}</td>
            <td>-</td>
            <td>£{excesses.windscreenRepairExcess}</td>
        </tr>
        </tbody>
    </table>
);


const ExcessExplanation: React.FC<{ vehicle: any, coverTier: string }> = ({
                                                                              vehicle: {annualVehicleExcess},
                                                                              coverTier: coverTier
                                                                          }) => (
    <>
        <p className="font-weight-extra-light mb-4">
            An excess is the amount you play towards any claim you make.
        </p>
        <p className="font-weight-extra-light mb-4">
            You have £{annualVehicleExcess.voluntaryExcess} voluntary excess and
            £{annualVehicleExcess.adExcess} compulsory excess for any accidental damage, fire or theft. See an example
            below.
        </p>
        {renderWolfExcessTableExplained(annualVehicleExcess, "compulsory")}
        <p className="font-weight-extra-light mb-4 additional-warning banner banner-info">
            * If you decide to use your own repairer rather than one we have recommended then an
            additional <b>{`£${annualVehicleExcess.nonRecommendedRepairerExcess}`}</b> excess will be applied to you your
            total excess (compulsory + voluntary)
        </p>
    </>
);

const DriverDetail: React.FC<{
    interactionId: string, vehicleIndex: number, driver: any, isMainDriver: boolean,
    isPolicyHolder: boolean, driverIndex?: number, disableLinks: boolean, canPerformMta: boolean
}> =
    ({interactionId, vehicleIndex, driver, isMainDriver, isPolicyHolder, driverIndex, disableLinks, canPerformMta}) => {

        const isFutureStartDate = moment().isBefore(driver.startDate);

        return (
            <Detail>
                <DetailKey>
                    {`${driver.firstName} ${driver.lastName}${isMainDriver ? " (main driver)" : ""}`}
                </DetailKey>
                <div className="text-center">
                    {canPerformMta && !disableLinks && <PanelLink
                        id={`vehicle-${vehicleIndex}-edit-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                        aria-label="View or edit driver"
                        to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${driverIndex !== undefined ? driverIndex : "PolicyHolder"}/${interactionId}`,
                            disableLinks)}
                        className={getCursorClassName(disableLinks)}
                    >
                        {<LinkHighlight>View</LinkHighlight>}
                        {" / "}
                        {isPolicyHolder && <LinkHighlight>Edit details</LinkHighlight>}
                    </PanelLink>
                    }
                    {isPolicyHolder && !canPerformMta && <span>View / Edit details</span>}
                    {!isPolicyHolder && canPerformMta && !disableLinks && <PanelLink
                        id={`vehicle-${vehicleIndex}-remove-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                        aria-label="View or edit driver"
                        to={getLink(`/journey/show/product/PropositionWolfMotorInsurance/process/mtaRemoveDriver/removeDriver${driverIndex}EntryPage/${interactionId}`,
                            disableLinks)}
                        className={getCursorClassName(disableLinks)}
                    >
                        <LinkHighlight>Remove driver</LinkHighlight>
                    </PanelLink>}
                    {!isPolicyHolder && !canPerformMta && <span>View / Remove driver</span>}
                    {!isMainDriver && isFutureStartDate &&
                        <p>* Cover start date: {moment(driver.startDate).format("YYYY-MM-DD")}</p>}
                </div>
            </Detail>
        );
    }

const DriversSectionContents: React.FC<{
    interaction: any,
    interactionId: string,
    vehicleIndex: number,
    disableLinks: boolean,
    canPerformMta: boolean
}> =
    ({interaction, interactionId, vehicleIndex, disableLinks, canPerformMta}) => {

        const vehicle = interaction.vehicles[vehicleIndex];

        const allDrivers: any[] = [interaction.policyHolder];
        if (interaction.additionalDrivers) {
            allDrivers.push(...interaction.additionalDrivers);
        }

        const vehicleDrivers: any[] = vehicle.driverIds.map(
            (driverId: string) => allDrivers.find(
                (driver: any) => driverId === driver.personId
            )
        );

        const additionalDrivers: any[] = vehicleDrivers.filter(
            (driver: any) => driver.personId !== interaction.policyHolder.personId
        )

        return (
            <SectionContents>
                <DriverDetail
                    interactionId={interactionId}
                    vehicleIndex={vehicleIndex}
                    driver={interaction.policyHolder}
                    isMainDriver={interaction.policyHolder.personId === vehicle.mainDriverId}
                    disableLinks={disableLinks}
                    canPerformMta={canPerformMta}
                    isPolicyHolder={true}
                />
                {additionalDrivers.map(
                    (driver: any, i: number) =>
                        <DriverDetail
                            key={i}
                            interactionId={interactionId}
                            vehicleIndex={vehicleIndex}
                            driver={driver}
                            driverIndex={i}
                            isMainDriver={driver.personId === vehicle.mainDriverId}
                            isPolicyHolder={false}
                            disableLinks={disableLinks}
                            canPerformMta={canPerformMta}
                        />)
                }
            </SectionContents>
        );
    }

interface IWolfYourCarPanelProps {
    interaction: any;
    currentlyActiveInteraction?: any;
    vehicleIndex: number;
    subscription: Subscription;
    annualPolicyInfo: AnnualPolicyInfo;
    policyEndDate: string;
}

interface IWolfYourCarPanelState {
    greenCards: GreenCard[]
}

export class AnnualYourCarPanel extends React.Component<IWolfYourCarPanelProps, IWolfYourCarPanelState> {

    private readonly vehicle: any;

    private readonly maxDriversCount: number = 6;

    private readonly alertMessage: string | undefined = undefined;

    private readonly interactionToRender: any;

    private readonly isBritanniaBrand = () => ["bn", "britannia"].includes((resourcesHelper.THEME || "").toLowerCase());


    constructor(props: IWolfYourCarPanelProps) {
        super(props);

        this.interactionToRender = this.props.currentlyActiveInteraction || this.props.interaction;

        if (this.props.annualPolicyInfo.hasDebt) {
            this.alertMessage = "Due to missed payment this action is not allowed";
        } else if (this.props.subscription && this.props.subscription.allAnnualMotorPoliciesAreCancelled) {
            this.alertMessage = "Sorry, due to cancellation this action is not allowed.";
        }

        this.vehicle = this.interactionToRender.vehicles[this.props.vehicleIndex!];

        this.state = {
            greenCards: []
        };
    }

    public async componentDidMount() {
    }

    public render() {
        const {interaction, vehicleIndex} = this.props;
        const driversCount: number = interaction.vehicles[0].driverIds.length;
        return (
            <div
                id={`your-car-panel-${vehicleIndex}`}
                className="section-panel your-car-panel mb-5"
            >
                <img className="main-icon mb-2 car-front-icon" src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                     alt="Vehicle icon"/>
                <Section className="car-details-section">
                    <div
                        className={`section-heading car-heading text-blue-green font-family-display ${
                            this.isBritanniaBrand() ? "" : "fs-24 font-weight-medium"
                        }`}
                    >
                        {`${capitalize(this.vehicle.make)}, ${capitalize(this.vehicle.model)}`}
                    </div>
                    <SectionContents>
                        <Detail>
                            <div
                                className={`number-plate-preview px-2 d-block mx-auto mb-2 ${
                                    this.isBritanniaBrand() ? "number-plate-preview--britannia" : ""
                                }`}
                            >
                                {this.vehicle.registrationNumber}
                            </div>
                        </Detail>
                    </SectionContents>
                    <SectionLinks>
                        <ButtonLink
                            id={`edit-car-${vehicleIndex}`}
                            aria-label="edit car or cover"
                            primaryNoArrow={true}
                            to={this.isDisabled() ? "" : `/journey/init/product/PropositionWolfMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${interaction._id}`}
                            disabled={this.isDisabled()}
                            alertMessage={this.alertMessage}
                        >Edit car or cover</ButtonLink>
                        <p className="mb-0 text-center font-weight-extra-light">
                            or get a quote to replace it
                        </p>
                        <ButtonLink
                            id={`replace-car-${vehicleIndex}`}
                            className={`${interaction._id}`}
                            aria-label="Replace car"
                            to={this.isDisabled() ? "" : `/journey/init/product/AnnualMotorInsurance/process/mtaReplaceCar/preset/${interaction._id}`}
                            disabled={this.isDisabled()}
                            alertMessage={this.alertMessage}
                        >Change this car</ButtonLink>
                    </SectionLinks>
                </Section>
                <Section className="drivers-section">
                    <SectionHeading>Driver(s)</SectionHeading>
                    <DriversSectionContents
                        interaction={interaction}
                        interactionId={interaction._id}
                        vehicleIndex={vehicleIndex}
                        disableLinks={this.disableLinks()}
                        canPerformMta={!this.props.annualPolicyInfo.maxMtaDateReached}
                    />
                    {driversCount < this.maxDriversCount && <SectionLinks>
                        <ButtonLink
                            id={`add-another-driver-${vehicleIndex}`}
                            primary={false}
                            to={`/journey/init/product/AnnualMotorInsurance/process/mtaAddPermanentDriver/preset/${interaction._id}`}
                            disabled={this.isDisabled()}
                            alertMessage={this.alertMessage}
                        >Add another driver</ButtonLink>
                    </SectionLinks>}
                </Section>
                <Section className="excess-section">
                    <SectionHeading className="position-relative mb-4 d-flex">
                        Excess that apply
                        <InfoModal
                            className="ml-3"
                            title="Excess explained"
                        >
                            <ExcessExplanation vehicle={this.vehicle} coverTier={this.interactionToRender.coverTier}/>
                        </InfoModal>
                    </SectionHeading>
                    <SectionContents>
                        <Detail>
                            <DetailKey>
                                Accidental damage:
                            </DetailKey>
                            <DetailValuePrice>
                                {`£${this.vehicle.annualVehicleExcess.compulsoryExcess + this.vehicle.annualVehicleExcess.voluntaryExcess} (this includes £${this.vehicle.annualVehicleExcess.voluntaryExcess} voluntary excess)`}
                            </DetailValuePrice>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Fire or theft:
                            </DetailKey>
                            <DetailValuePrice>
                                {`£${this.vehicle.annualVehicleExcess.compulsoryExcess + this.vehicle.annualVehicleExcess.voluntaryExcess} (this includes £${this.vehicle.annualVehicleExcess.voluntaryExcess} voluntary excess)`}
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"font-weight-semi-bold"}>
                            Glass-only excess costs you less
                        </Detail>
                        <Detail className={"pt-0"}>
                            <DetailKey>
                                Glass repair excess:
                            </DetailKey>
                            <DetailValuePrice>
                                <span
                                    className="font-weight-normal">just</span> {`£${this.vehicle.annualVehicleExcess.windscreenRepairExcess}`}
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"pt-0"}>
                            <DetailKey>
                                Glass replacement excess:
                            </DetailKey>
                            <DetailValuePrice>
                                <span
                                    className="font-weight-normal">just</span> {`£${this.vehicle.annualVehicleExcess.windscreenReplacementExcess}`}
                            </DetailValuePrice>
                        </Detail>
                        <Detail className={"non-recommended-excess mt-2 mb-0"}>
                            <DetailValue>
                                <div className="font-weight-semi-bold">Non-recommended repairer excess costs you more
                                </div>
                            </DetailValue>
                            <DetailKey>Non-recommended repairer excess:</DetailKey>
                            <DetailValue>
                                <span
                                    className="font-weight-bold">{`£${this.vehicle.annualVehicleExcess.nonRecommendedRepairerExcess}`}</span>
                            </DetailValue>
                        </Detail>
                        <Detail className={"my-0 py-0"}>
                            <div>This is in addition to the excesses shown above if you choose a garage <span
                                className="font-weight-semi-bold">not</span> on our list of recommended repairers.<br/>
                                This also applies to glass replacement but <span
                                    className="font-weight-semi-bold">not</span> glass-only repairs.
                            </div>
                        </Detail>
                    </SectionContents>
                </Section>
                <Section className="optional-extras-section">
                    <SectionHeading>Optional extras</SectionHeading>
                    <SectionContents>
                        <Detail>
                            <DetailKey>
                                Protected no claim discount:
                            </DetailKey>
                            <DetailValue>
                                {this.interactionToRender.policyHolder.ncd.doNotDisclose ? "Included" : "Not included"}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Breakdown cover:
                            </DetailKey>
                            <DetailValue>
                                {this.renderBreakdown(this.interactionToRender.optionalExtras)}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Motor legal expenses:
                            </DetailKey>
                            <DetailValue>
                                {getIsOptionalExtraIncludedText(this.interactionToRender.coverTier, this.interactionToRender.optionalExtras.motorLegalExpenses)}
                            </DetailValue>
                        </Detail>
                        {!this.isBritanniaBrand() &&
                            <Detail>
                                <DetailKey>
                                    {getGHCIncludeDescription(this.interactionToRender.coverTier)}
                                </DetailKey>
                                <DetailValue>
                                    {getIsOptionalExtraIncludedText(this.interactionToRender.coverTier, this.interactionToRender.optionalExtras.courtesyCar)}
                                </DetailValue>
                            </Detail>
                        }
                        {this.isBritanniaBrand() && (
                            <>
                                <Detail>
                                    <DetailKey>
                                        {getGHCIncludeDescription(this.interactionToRender.coverTier)}
                                    </DetailKey>
                                    <DetailValue>
                                        {getCourtesyCarLevelIncludedText(
                                            this.interactionToRender.coverTier,
                                            this.interactionToRender.optionalExtras
                                        )}
                                    </DetailValue>
                                </Detail>

                                <Detail>
                                    <DetailKey>
                                        Guaranteed hire car:
                                    </DetailKey>
                                    <DetailValue>
                                        {getGuaranteedHireCarText(
                                            this.interactionToRender.coverTier,
                                            this.interactionToRender.optionalExtras
                                        )}
                                    </DetailValue>
                                </Detail>
                            </>
                        )}
                        {InteractionHelper.isPolicyQuotedBeforeFamilyCoverRemoval(this.interactionToRender) && <Detail>
                            <DetailKey>
                                Family Package:
                            </DetailKey>
                            <DetailValue>
                                {this.interactionToRender.optionalExtras.familyPackage ? "Included" : "Not included"}
                            </DetailValue>
                        </Detail>}
                        <Detail>
                            <SectionLinks>
                                    {!this.props.annualPolicyInfo.maxMtaDateReached &&
                                        <ButtonLink
                                            id={`edit-optional-extras-${vehicleIndex}`}
                                            primary={false}
                                            to={this.isDisabled() ? "" : `/journey/init/product/PropositionWolfMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${interaction._id}`}
                                            disabled={this.isDisabled()}
                                            alertMessage={this.alertMessage}
                                        >Edit optional extras</ButtonLink>}
                            </SectionLinks>
                        </Detail>
                    </SectionContents>
                </Section>
                {this.isBritanniaBrand() && (
                    <Section className="green-card-section">
                        <SectionHeading>Green card</SectionHeading>
                        <SectionContents>
                            <Detail>
                                <DetailValue>
                                    Did you know that UK are now part of the <span className="font-weight-semi-bold">Green Card Free Circulation Area</span>?
                                    This now means that if you are driving within the EEA (European Economic Area)
                                    you no longer need to carry with you a green card. That’s one less thing to worry about.
                                </DetailValue>
                            </Detail>
                        </SectionContents>
                    </Section>
                )}
            </div>
        );
    }

    private renderBreakdown(optionalExtras: any) {
        if (optionalExtras.breakdownCover === false) {
            return 'Not included'
        }
        switch (optionalExtras.breakdownCoverLevel) {
            case BreakdownCoverLevelEnum.LOCAL:
                return 'Included (Local)';
            case BreakdownCoverLevelEnum.UK:
                return 'Included (National)';
            case BreakdownCoverLevelEnum.EU:
                return 'Included (European)';
            default:
                throw new Error(`Unknown Breakdown Cover level: ${optionalExtras.breakdownCoverLevel}`);
        }
    }

    private isDisabled() {
        return this.props.annualPolicyInfo.maxMtaDateReached || this.disableLinks();
    }

    private disableLinks(): boolean {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange, maxFutureMtaDate} = this.props.annualPolicyInfo;
        const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(maxFutureMtaDate), 'day');
        if (this.props.subscription) {
            const {allAnnualMotorPoliciesAreCancelled} = this.props.subscription;
            return hasDebt || allAnnualMotorPoliciesAreCancelled || hasUnpaidChargebackInvoice || hasUnpaidChange || mtaOnEndDateOfPolicy;
        } else {
            return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange || mtaOnEndDateOfPolicy;
        }
    }
}
